import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  listClasses,
  createClass,
  updateClass,
  listSubjectCombinations,
  listStaff,
  listMySchools,
} from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

const CLASS_TYPES = ["", "LỚP CHỌN", "LỚP THƯỜNG"];
const STATUS_OPTIONS = [
  { value: "", label: "Đang học" },
  { value: "giai_tan", label: "Đã giải tán" },
];
const EXPORT_COLS = ["Lớp", "Khoá", "Niên khoá", "Loại lớp", "GV chủ nhiệm", "Tổ hợp", "Đi lại", "Ghi chú", "Phòng học VH", "Trạng thái"];
const norm = (s) => String(s || "").trim().toLowerCase();

const HappySchoolClassList = () => {
  const schoolId = getActiveSchoolId();
  const [classes, setClasses] = useState([]);
  const [combos, setCombos] = useState([]);
  const [staff, setStaff] = useState([]);
  const [khoi, setKhoi] = useState("12");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [ioStatus, setIoStatus] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  // Hộp sửa "GVCN theo thời gian": { classId, className, rows: [{ teacherId, from, to }] }
  const [hrEdit, setHrEdit] = useState(null);
  const fileRef = useRef(null);

  // Chỉ Owner của trường mới được nhập Excel (ghi đè hàng loạt).
  useEffect(() => {
    if (!schoolId) { setIsOwner(false); return; }
    listMySchools()
      .then((list) => {
        const s = (list || []).find((x) => x.id === schoolId);
        setIsOwner(!!s && s.role === "owner");
      })
      .catch(() => setIsOwner(false));
  }, [schoolId]);

  const reload = () => {
    if (!schoolId) return;
    setLoading(true);
    Promise.all([listClasses(schoolId), listSubjectCombinations(schoolId), listStaff(schoolId)])
      .then(([c, combos, st]) => {
        setClasses(c);
        setCombos(combos);
        setStaff(st.slice().sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [schoolId]);

  if (!schoolId) {
    return (
      <div style={{ padding: 24 }}>
        Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link> trước.
      </div>
    );
  }

  const filteredClasses = classes
    .filter((c) => !khoi.trim() || c.name.startsWith(khoi.trim()))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  const gradeOf = (nm) => (String(nm).match(/^(\d+)/) || [])[1] || "";
  const counts = { "12": 0, "11": 0, "10": 0 };
  classes.forEach((c) => { const g = gradeOf(c.name); if (counts[g] !== undefined) counts[g] += 1; });
  const khoiButtons = [
    { label: "Tất cả", val: "", count: classes.length },
    { label: "Khối 12", val: "12", count: counts["12"] },
    { label: "Khối 11", val: "11", count: counts["11"] },
    { label: "Khối 10", val: "10", count: counts["10"] },
  ];

  const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "");
  const periodText = (e) => `${e.from ? "từ " + fmtDate(e.from) : "từ đầu"}${e.to ? " đến " + fmtDate(e.to) : " đến nay"}`;
  const staffName = (id) => (staff.find((t) => t.id === id) || {}).name || id;
  const openHrEdit = (c) => {
    const hist = (c.homeroomHistory || []).map((e) => ({ teacherId: e.teacherId, from: e.from || "", to: e.to || "" }));
    // Lớp chưa có lịch sử: dòng đầu = GVCN hiện tại, không giới hạn ngày.
    setHrEdit({ classId: c.id, className: c.name, rows: hist.length ? hist : [{ teacherId: c.homeroomTeacherId || "", from: "", to: "" }] });
  };
  const saveHrEdit = async () => {
    const rows = hrEdit.rows.filter((r) => r.teacherId);
    try {
      await updateClass(schoolId, hrEdit.classId, { homeroomHistory: rows });
      setHrEdit(null);
      reload();
    } catch (err) {
      setError(err.message);
    }
  };
  const setHrRow = (i, patch) => setHrEdit((h) => ({ ...h, rows: h.rows.map((r, k) => (k === i ? { ...r, ...patch } : r)) }));

  const handleFieldChange = async (classId, field, value) => {
    try {
      await updateClass(schoolId, classId, { [field]: value });
      reload();
    } catch (err) {
      setError(err.message);
    }
  };

  // Xuất danh sách lớp (đang lọc theo Khối) ra Excel. GV chủ nhiệm & Tổ hợp xuất theo TÊN để dễ đọc/nhập lại.
  const handleExport = () => {
    const staffById = Object.fromEntries(staff.map((t) => [t.id, t]));
    const comboById = Object.fromEntries(combos.map((co) => [co.id, co]));
    const rows = filteredClasses.map((c) => [
      c.name,
      c.cohort || "",
      c.cohortYears || "",
      c.classType || "",
      c.homeroomTeacherId && staffById[c.homeroomTeacherId] ? staffById[c.homeroomTeacherId].name : "",
      c.combinationId && comboById[c.combinationId] ? comboById[c.combinationId].name : "",
      c.transport || "",
      c.note || "",
      c.room || "",
      c.status === "giai_tan" ? "Đã giải tán" : "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([EXPORT_COLS, ...rows]);
    ws["!cols"] = [80, 60, 100, 110, 160, 140, 90, 160, 100].map((w) => ({ wpx: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Lớp học");
    XLSX.writeFile(wb, `DanhSachLop_Khoi${khoi.trim() || "all"}.xlsx`);
  };

  // Nhập Excel: cột theo đúng thứ tự tiêu đề xuất. Khớp lớp theo tên (lớp mới sẽ được tạo);
  // GV chủ nhiệm & Tổ hợp khớp theo tên. Ô để trống nghĩa là giữ nguyên (không ghi đè).
  const handleImport = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setIoStatus("Đang đọc file...");
    setError("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (aoa.length < 2) { setIoStatus("File rỗng."); return; }

      const staffByName = {};
      staff.forEach((t) => { staffByName[norm(t.name)] = t; });
      const comboByName = {};
      combos.forEach((co) => { comboByName[norm(co.name)] = co; });
      const validTypes = new Set(CLASS_TYPES.map(norm));

      // Tạo trước các lớp còn thiếu rồi nạp lại để lấy id.
      let classByName = {};
      classes.forEach((c) => { classByName[norm(c.name)] = c; });
      const missing = [];
      for (let r = 1; r < aoa.length; r++) {
        const nm = String((aoa[r] || [])[0] || "").trim();
        if (nm && !classByName[norm(nm)]) missing.push(nm);
      }
      if (missing.length) {
        setIoStatus(`Tạo ${missing.length} lớp mới...`);
        for (const nm of [...new Set(missing)]) {
          try { await createClass(schoolId, nm); } catch (_) { /* bỏ qua nếu trùng */ }
        }
        const fresh = await listClasses(schoolId);
        classByName = {};
        fresh.forEach((c) => { classByName[norm(c.name)] = c; });
      }

      let ok = 0, skipped = 0;
      const ops = [];
      for (let r = 1; r < aoa.length; r++) {
        const row = aoa[r] || [];
        const className = String(row[0] || "").trim();
        if (!className) continue;
        const cls = classByName[norm(className)];
        if (!cls) { skipped++; continue; }

        const patch = {};
        const cohort = String(row[1] || "").trim();
        const cohortYears = String(row[2] || "").trim();
        const classType = String(row[3] || "").trim();
        const gvName = String(row[4] || "").trim();
        const tohopName = String(row[5] || "").trim();
        const transport = String(row[6] || "").trim();
        const note = String(row[7] || "").trim();
        const room = String(row[8] || "").trim();
        const statusText = String(row[9] || "").trim();

        if (cohort) patch.cohort = cohort;
        if (cohortYears) patch.cohortYears = cohortYears;
        if (classType && validTypes.has(norm(classType))) patch.classType = classType.toUpperCase();
        if (gvName) { const t = staffByName[norm(gvName)]; if (t) patch.homeroomTeacherId = t.id; }
        if (tohopName) { const co = comboByName[norm(tohopName)]; if (co) patch.combinationId = co.id; }
        if (transport) patch.transport = transport;
        if (note) patch.note = note;
        if (room) patch.room = room;
        if (statusText) patch.status = norm(statusText).includes("giải tán") || norm(statusText).includes("giai tan") ? "giai_tan" : "";

        if (Object.keys(patch).length === 0) continue;
        ops.push(updateClass(schoolId, cls.id, patch).then(() => { ok++; }).catch(() => { skipped++; }));
      }
      setIoStatus(`Đang lưu ${ops.length} lớp...`);
      await Promise.all(ops);
      setIoStatus(`Xong: cập nhật ${ok} lớp${skipped ? `, bỏ qua ${skipped}` : ""}.`);
      reload();
    } catch (err) {
      setError("Lỗi nhập Excel: " + err.message);
      setIoStatus("");
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 16px 0" }}>Lớp học</h2>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        {khoiButtons.map((b) => (
          <button
            key={b.label}
            type="button"
            onClick={() => setKhoi(b.val)}
            style={khoi === b.val ? khoiBtnActiveStyle : khoiBtnStyle}
          >
            {b.label} ({b.count})
          </button>
        ))}
        <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
          <button type="button" onClick={handleExport} style={ioBtnStyle} title="Tải danh sách lớp ra Excel">
            ⬇️ Xuất Excel
          </button>
          {isOwner && (
            <>
              <button type="button" onClick={() => fileRef.current && fileRef.current.click()} style={ioBtnStyle} title="Nhập danh sách lớp từ Excel (chỉ Owner)">
                ⬆️ Nhập Excel
              </button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleImport} style={{ display: "none" }} />
            </>
          )}
        </div>
      </div>
      {ioStatus && <p style={{ color: "#0f766e", fontSize: 13, margin: "0 0 12px" }}>{ioStatus}</p>}

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {loading ? (
        <p>Đang tải...</p>
      ) : filteredClasses.length === 0 ? (
        <p style={{ color: "#6b7280" }}>Không có lớp nào khớp Khối "{khoi}".</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={thStyle}>Lớp</th>
              <th style={thStyle}>Khoá</th>
              <th style={thStyle}>Niên khoá</th>
              <th style={thStyle}>Loại lớp</th>
              <th style={thStyle}>Phòng học VH</th>
              <th style={thStyle}>GV chủ nhiệm</th>
              <th style={thStyle}>Tổ hợp</th>
              <th style={thStyle}>Đi lại</th>
              <th style={thStyle}>Ghi chú</th>
              <th style={thStyle}>Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            {filteredClasses.map((c) => (
              <tr key={c.id} style={c.status === "giai_tan" ? { opacity: 0.55 } : undefined}>
                <td style={{ ...tdStyle, textAlign: "left" }}>
                  <strong>{c.name}</strong>
                  {c.status === "giai_tan" && (
                    <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#dc2626" }}>GIẢI TÁN</span>
                  )}
                </td>
                <td style={tdStyle}>
                  <input
                    defaultValue={c.cohort || ""}
                    placeholder="K24"
                    onBlur={(e) => handleFieldChange(c.id, "cohort", e.target.value)}
                    style={{ ...inputStyle, width: 60 }}
                  />
                </td>
                <td style={tdStyle}>
                  <input
                    defaultValue={c.cohortYears || ""}
                    placeholder="2024–2027"
                    onBlur={(e) => handleFieldChange(c.id, "cohortYears", e.target.value)}
                    style={{ ...inputStyle, width: 100 }}
                  />
                </td>
                <td style={tdStyle}>
                  <select
                    defaultValue={c.classType || ""}
                    onChange={(e) => handleFieldChange(c.id, "classType", e.target.value)}
                    style={selectStyle}
                  >
                    {CLASS_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t || "—"}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={tdStyle}>
                  <input
                    defaultValue={c.room || ""}
                    placeholder="P.502.A3"
                    onBlur={(e) => handleFieldChange(c.id, "room", e.target.value)}
                    style={{ ...inputStyle, width: 100 }}
                  />
                </td>
                <td style={tdStyle}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 190 }}>
                    {(c.homeroomHistory || []).length === 0 ? (
                      <span style={{ fontSize: 13 }}>
                        {c.homeroomTeacherId ? staffName(c.homeroomTeacherId) : "—"}
                        <span style={{ color: "#9ca3af" }}> (không giới hạn ngày)</span>
                      </span>
                    ) : (
                      c.homeroomHistory.map((e, i) => (
                        <span key={i} style={{ fontSize: 13 }}>
                          <b>{staffName(e.teacherId)}</b>
                          <span style={{ color: "#6b7280" }}> — {periodText(e)}</span>
                        </span>
                      ))
                    )}
                    <button type="button" onClick={() => openHrEdit(c)} style={{ ...ioBtnStyle, padding: "2px 8px", fontSize: 12, alignSelf: "flex-start" }}>
                      📅 Sửa GVCN & thời gian
                    </button>
                  </div>
                </td>
                <td style={tdStyle}>
                  <select
                    defaultValue={c.combinationId || ""}
                    onChange={(e) => handleFieldChange(c.id, "combinationId", e.target.value || null)}
                    style={selectStyle}
                  >
                    <option value="">—</option>
                    {combos.map((combo) => (
                      <option key={combo.id} value={combo.id}>
                        {combo.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={tdStyle}>
                  <input
                    defaultValue={c.transport || ""}
                    onBlur={(e) => handleFieldChange(c.id, "transport", e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td style={tdStyle}>
                  <input
                    defaultValue={c.note || ""}
                    onBlur={(e) => handleFieldChange(c.id, "note", e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td style={tdStyle}>
                  <select
                    defaultValue={c.status || ""}
                    onChange={(e) => handleFieldChange(c.id, "status", e.target.value)}
                    style={selectStyle}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {hrEdit && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 20, width: 620, maxWidth: "95vw", maxHeight: "90vh", overflow: "auto" }}>
            <h3 style={{ margin: "0 0 6px" }}>GVCN lớp {hrEdit.className} theo thời gian</h3>
            <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 12px" }}>
              Mỗi dòng là một giáo viên chủ nhiệm trong một khoảng ngày (tính cả ngày đầu và ngày cuối). Để trống "Từ ngày" = từ đầu, trống "Đến ngày" = đến nay. Các khoảng không được chồng nhau. Chấm công tiết chủ nhiệm tính theo đúng khoảng ngày này.
            </p>
            {hrEdit.rows.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <select value={r.teacherId} onChange={(e) => setHrRow(i, { teacherId: e.target.value })} style={{ ...selectStyle, flex: 2 }}>
                  <option value="">— Chọn giáo viên —</option>
                  {staff.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                </select>
                <label style={{ fontSize: 12, flex: 1 }}>Từ ngày
                  <input type="date" value={r.from} onChange={(e) => setHrRow(i, { from: e.target.value })} style={inputStyle} />
                </label>
                <label style={{ fontSize: 12, flex: 1 }}>Đến ngày
                  <input type="date" value={r.to} onChange={(e) => setHrRow(i, { to: e.target.value })} style={inputStyle} />
                </label>
                <button type="button" title="Xoá dòng" onClick={() => setHrEdit((h) => ({ ...h, rows: h.rows.filter((_, k) => k !== i) }))} style={{ ...ioBtnStyle, color: "#dc2626", borderColor: "#fca5a5" }}>✕</button>
              </div>
            ))}
            <button type="button" onClick={() => setHrEdit((h) => ({ ...h, rows: [...h.rows, { teacherId: "", from: "", to: "" }] }))} style={ioBtnStyle}>+ Thêm khoảng</button>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button type="button" onClick={() => setHrEdit(null)} style={ioBtnStyle}>Huỷ</button>
              <button type="button" onClick={saveHrEdit} style={{ ...ioBtnStyle, background: "#0ea5a3", color: "#fff" }}>Lưu</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = { border: "1px solid #e5e7eb", padding: "8px 10px", background: "#f9fafb", textAlign: "left", fontSize: 13 };
const tdStyle = { border: "1px solid #e5e7eb", padding: "6px 8px" };
const inputStyle = { width: "100%", padding: 4, border: "1px solid #d1d5db", borderRadius: 4, boxSizing: "border-box" };
const selectStyle = { width: "100%", padding: 4, border: "1px solid #d1d5db", borderRadius: 4 };
const khoiBtnStyle = { padding: "6px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 500 };
const khoiBtnActiveStyle = { ...khoiBtnStyle, background: "#0ea5a3", color: "#fff", borderColor: "#0ea5a3", fontWeight: 700 };
const ioBtnStyle = { padding: "6px 12px", background: "#fff", color: "#0f766e", border: "1px solid #0ea5a3", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 };

export default HappySchoolClassList;
